import { useCallback, useEffect, useRef, useState } from "react";
import type { Layer } from "@/domain/types";
import {
  type BoardPartFile,
  type ImportedKeyboard,
  importKeyboard,
  importKeyboardPair,
  partLabel,
  partnerLabel,
} from "@/hardware/keyboards/keyboard-import";
import { isEditableTarget } from "@/platform/lib/editable-target";
import { useToasts } from "@/render/components/Toasts";
import { type DetectedContent, detectContent } from "./detect";
import {
  buildKeyboardPreview,
  buildProfilePreview,
  countUnmappedKeys,
  type ImportPreview,
} from "./preview";
import {
  type ImportedProfile,
  importProfile,
  profileBindingCount,
  sc2BindingCounts,
} from "./profile-import";

export type PendingImport =
  | { kind: "keyboard"; result: ImportedKeyboard; preview: ImportPreview }
  | { kind: "profile"; result: ImportedProfile; preview: ImportPreview };

/** Which upload opened the modal: the Profile (hotkey) target or the Keyboard target. */
export type ImportTarget = "profile" | "keyboard";

/** A file whose kind does not match the upload that opened the modal: say where it belongs. */
function targetMismatch(target: ImportTarget, kind: DetectedContent["kind"]): string | null {
  if (target === "profile" && (kind === "keyboard" || kind === "board-part")) {
    return "That looks like a keyboard file. Use the Keyboard upload (beside the Keyboard picker) for layouts; this one takes a hotkey profile.";
  }
  if (target === "keyboard" && kind === "profile") {
    return "That looks like a hotkey profile. Use the Profile upload (beside the Profile picker); this one takes a keyboard file (KLE / VIA / QMK).";
  }
  return null;
}

// A keyboard or profile file is a few KB; this only rejects abusive uploads before we read the whole
// thing into a string, so a multi-megabyte drop can't tie up memory or the parse.
const MAX_IMPORT_BYTES = 4 * 1024 * 1024;

/** The awaiting-counterpart line for a parked half of a two-file board upload. */
function partMessage(part: BoardPartFile): string {
  const who = part.name ? `${partLabel(part.format)} "${part.name}"` : partLabel(part.format);
  return `${who} received — now add the ${partnerLabel(part.format)} file.`;
}

/**
 * The import-pipeline shell: capture (drop anywhere, paste, modal), detect, preview, apply,
 * toast. Preview coverage counts against the Profile's base layer, stable across mod swaps.
 * It handles VIA/QMK board-part pairing and SC2 Profiles across the
 * detect and preview stages; this hook stays the one shell.
 */
export function useImportPipeline(
  baseLayer: Layer,
  onApply: (applied: PendingImport) => void,
  initialImportCount = 0,
) {
  const { toasts, push, dismiss } = useToasts();
  const [modalOpen, setModalOpen] = useState(false);
  const [pending, setPending] = useState<PendingImport | null>(null);
  const [error, setError] = useState<string | null>(null);
  // the upload that opened the modal (Profile vs Keyboard), or null for a global drop/paste
  // (no button, no scope). A set target rejects a file of the other kind.
  const [target, setTarget] = useState<ImportTarget | null>(null);
  const [held, setHeld] = useState<BoardPartFile | null>(null);
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  // seeded past any restored imported board so a new upload never reuses a persisted
  // imported-N id; 0 for a fresh session. Read once at mount, then incremented per import.
  const importCount = useRef(initialImportCount);

  const ingest = useCallback(
    (items: { text: string; fileName?: string }[]) => {
      let aw = held;
      let failure: string | null = null;
      let imported: PendingImport | null = null;
      for (const item of items) {
        try {
          const detected = detectContent(item.text);
          const mismatch = target ? targetMismatch(target, detected.kind) : null;
          if (mismatch) throw new Error(mismatch);
          if (detected.kind === "profile") {
            const result = importProfile(detected.text, {
              fileName: item.fileName,
              format: detected.format,
            });
            imported = {
              kind: "profile",
              result,
              preview: buildProfilePreview(result, item.fileName),
            };
            aw = null;
          } else if (detected.kind === "board-part") {
            const part: BoardPartFile = {
              format: detected.format,
              data: detected.data,
              name: detected.name,
              fileName: item.fileName,
            };
            if (aw && aw.format !== part.format) {
              // counterpart or mismatch: importKeyboardPair throws the pairing error itself
              const result = importKeyboardPair(aw, part, {
                id: `imported-${importCount.current + 1}`,
              });
              importCount.current += 1;
              const joined = [aw.fileName, part.fileName].filter(Boolean).join(" + ");
              imported = {
                kind: "keyboard",
                result,
                preview: buildKeyboardPreview(result, baseLayer, joined || undefined),
              };
              aw = null;
            } else {
              // first half of a pair, or a same-format replacement (newest wins)
              aw = part;
            }
          } else if (detected.kind === "keyboard") {
            const result = importKeyboard(detected, {
              id: `imported-${importCount.current + 1}`,
              fileName: item.fileName,
            });
            importCount.current += 1;
            imported = {
              kind: "keyboard",
              result,
              preview: buildKeyboardPreview(result, baseLayer, item.fileName),
            };
            aw = null;
          } else {
            throw new Error(detected.reason); // unknown content; the detect stage explains why
          }
        } catch (e) {
          failure = e instanceof Error ? e.message : String(e);
        }
      }
      // a completed import wins over a parked half regardless of arrival order
      if (imported) aw = null;
      setHeld(aw);
      if (imported) {
        setPending(imported);
        setError(null);
        setModalOpen(true);
      } else if (failure) {
        if (modalOpen) {
          // drop back to the idle state so the kl-drop-error row is actually visible
          // (the preview branch renders no error surface)
          setPending(null);
          setError(failure);
        } else {
          push({ kind: "error", title: "Couldn't read that file", body: failure });
        }
      } else if (aw) {
        setPending(null);
        setError(null);
        setModalOpen(true);
      }
    },
    [baseLayer, held, modalOpen, push, target],
  );

  const handleText = useCallback(
    (text: string, fileName?: string) => ingest([{ text, fileName }]),
    [ingest],
  );

  const handleFiles = useCallback(
    (files: File[] | FileList) => {
      const list = Array.from(files);
      if (!list.length) return;
      const tooBig = list.find((f) => f.size > MAX_IMPORT_BYTES);
      if (tooBig) {
        push({
          kind: "error",
          title: "File too large",
          body: `${tooBig.name || "That file"} is over ${MAX_IMPORT_BYTES / (1024 * 1024)} MB. Keyboard and profile files are normally a few KB.`,
        });
        return;
      }
      Promise.all(list.map(async (f) => ({ text: await f.text(), fileName: f.name }))).then(
        ingest,
        () =>
          push({
            kind: "error",
            title: "Couldn't read that file",
            body: "A dropped file could not be read.",
          }),
      );
    },
    [ingest, push],
  );

  const openModal = useCallback((next: ImportTarget) => {
    setTarget(next);
    setPending(null);
    setError(null);
    setHeld(null);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setTarget(null);
    setPending(null);
    setError(null);
    setHeld(null);
    setModalOpen(false);
  }, []);

  const apply = useCallback(() => {
    if (!pending) return;
    onApply(pending);
    if (pending.kind === "profile") {
      const r = pending.result;
      // the same counts the preview headlined, so the confirmation cannot contradict the preview
      let body: string;
      if (r.format === "sc2-hotkeys") {
        const c = sc2BindingCounts(r.table);
        body = `${r.name} — ${c.total} bindings loaded: ${c.global} global, ${c.card} command card. Switch the Keyboard Layout if keys look misplaced.`;
      } else {
        body = `${r.name} — ${profileBindingCount(r.profile)} bindings loaded. Switch the Keyboard Layout if keys look misplaced.`;
      }
      push({ kind: "ok", title: "Profile imported", body });
      closeModal();
      return;
    }
    const { keyboard, boardLayers, format } = pending.result;
    const unmapped = format === "kle" ? countUnmappedKeys(keyboard) : 0;
    if (unmapped > 0) {
      push({
        kind: "warn",
        title: "Keyboard imported with unmapped keys",
        body: `${keyboard.name} — ${unmapped} key${unmapped === 1 ? "" : "s"} shown as plain caps.`,
      });
    } else {
      const layers = boardLayers.length > 1 ? `, ${boardLayers.length} board layers` : "";
      push({
        kind: "ok",
        title: "Keyboard imported",
        body: `${keyboard.name} — ${keyboard.keys.length} keys${layers}.`,
      });
    }
    closeModal();
  }, [pending, onApply, push, closeModal]);

  // drag-and-drop anywhere on the page
  useEffect(() => {
    const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes("Files");
    const onDragEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      dragDepth.current += 1;
      setDragging(true);
    };
    const onDragOver = (e: DragEvent) => {
      if (hasFiles(e)) e.preventDefault();
    };
    const onDragLeave = () => {
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setDragging(false);
    };
    const onDrop = (e: DragEvent) => {
      // always clear the overlay, even for drops a more specific target already took
      dragDepth.current = 0;
      setDragging(false);
      if (!hasFiles(e)) return;
      const takenByModal = e.defaultPrevented; // the modal drop zone handled this one
      e.preventDefault();
      if (takenByModal) return;
      const files = e.dataTransfer?.files;
      if (files?.length) handleFiles(files);
    };
    document.addEventListener("dragenter", onDragEnter);
    document.addEventListener("dragover", onDragOver);
    document.addEventListener("dragleave", onDragLeave);
    document.addEventListener("drop", onDrop);
    return () => {
      document.removeEventListener("dragenter", onDragEnter);
      document.removeEventListener("dragover", onDragOver);
      document.removeEventListener("dragleave", onDragLeave);
      document.removeEventListener("drop", onDrop);
    };
  }, [handleFiles]);

  // paste anywhere: only act when the text actually detects as something importable,
  // the target is not an editable element, and the modal is not already open
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      if (modalOpen) return;
      if (isEditableTarget(e.target)) return;
      const text = e.clipboardData?.getData("text") ?? "";
      if (!text.trim()) return;
      if (detectContent(text).kind === "unknown") return;
      handleText(text);
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [modalOpen, handleText]);

  return {
    toasts,
    dismissToast: dismiss,
    pushToast: push,
    modalOpen,
    pending,
    error,
    target,
    awaiting: held ? partMessage(held) : null,
    dragging,
    handleText,
    handleFiles,
    apply,
    openModal,
    closeModal,
  };
}
